import Button from "@elements/button";
import GoogleMap from "@elements/googleMap";
import Marker from "@elements/marker";
import MapView from "@modules/address/mapView";
import Modal from "@modules/layout/modal";
import { IAreaDetailInput } from "@types";
import { useIsMount } from "@utils/helpers";
import { commonApi } from "gate";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { BsTrash, BsX } from "react-icons/bs";

interface MapFieldProps {
  useMap?: boolean;
  area: IAreaDetailInput;
  setUseMap?: (data: boolean) => void;
  defaultAddress: { lat: number; lng: number };
  onChange?: (data: { value: string | undefined; street: string; newFormValues: { [key: string]: any } }) => void;
  changeMapValue: (data: { lat: number; lng: number; street: string }) => void;
  label: string;
  isEdit: boolean
}

const MapField: React.FC<MapFieldProps> = ({
  useMap,
  setUseMap,
  defaultAddress,
  onChange,
  area,
  changeMapValue,
  label,
  isEdit
}) => {
  const t = useTranslations();
  const [markerPosition, setMarkerPosition] = useState({
    lat: 29.372182,
    lng: 47.982538,
  });
  const [center, setCenter] = useState({ lat: 29.372182, lng: 47.982538 });
  const [boundaryPoints, setBoundaryPoints] = useState<
    { lat: number; lng: number }[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [goToCurrentLocation, setGoToCurrentLocation] = useState<boolean>(isEdit ? false : true);
  const [openMapView, setOpenMapView] = useState<boolean>(false);
  const [mapValue, setMapValue] = useState<
    { street: string; lat: number; lng: number; newFormValues: { [key: string]: any } } | undefined
  >(undefined);
  const isMount = useIsMount();

  useEffect(() => {
    const getBoundary = async () => {
      setIsLoading(true);
      let boundary = await commonApi.getBoundary(area.id).fetch();
      setBoundaryPoints(boundary.data.boundary);
      setMarkerPosition(defaultAddress ? defaultAddress : area.center);
      setIsLoading(false);
    };
    getBoundary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area]);

  useEffect(() => {
    setIsLoading(true);
    setCenter(defaultAddress);
    setMarkerPosition(defaultAddress);
    setIsLoading(false);
  }, [defaultAddress]);

  useEffect(() => {
    if (mapValue && !isMount) {
      setIsLoading(true);
      setCenter({ lat: mapValue?.lat, lng: mapValue?.lng });
      setMarkerPosition({ lat: mapValue?.lat, lng: mapValue?.lng });
      onChange?.({
        value: useMap ? `${mapValue.lat},${mapValue.lng}` : undefined,
        street: mapValue.street,
        newFormValues: mapValue.newFormValues,
      });
      changeMapValue(mapValue);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useMap]);

  const submitMapValue = (value: {
    street: string;
    lat: number;
    lng: number;
    newFormValues: { [key: string]: any }
  }) => {
    setIsLoading(true);
    setGoToCurrentLocation(false)
    setCenter({ lat: value.lat, lng: value.lng });
    setMarkerPosition({ lat: value.lat, lng: value.lng });
    onChange?.({
      value: useMap ? `${value.lat},${value.lng}` : undefined,
      street: value.street,
      newFormValues: value.newFormValues,
    });
    changeMapValue(value);
    setMapValue(value);
    setIsLoading(false);
    setOpenMapView(false);
  };
  return (
    <div className="md:w-full sm:w-full xs:w-full w-full p-1 h-[216px]">
      {openMapView && (
        <Modal
          open={openMapView}
          onClose={() => setOpenMapView(false)}
          cancelable={false}
          withoutBackground
        >
          <div className="md:w-full sm:w-[calc(100vw-8px)] xs:w-[calc(100vw-8px)] w-[calc(100vw-8px)] max-w-[850px] min-h-[480px] h-[max-content] bg-white rounded-lg p-3 mx-auto">
            <div className=" sm:min-w-[420px]  xs:min-w-full min-w-full">
              <div className=" w-full flex flex-wrap mb-5 items-center">
                <p className="w-10 text-[#999]"></p>
                <p className="flex-1 font-normal text-xl text-[#333] text-center">
                  {t("client_buttons_confirmpin")}
                </p>
                <p className="w-10 text-[#999] flex justify-end">
                  <BsX
                    size={24}
                    className=" cursor-pointer"
                    onClick={() => { setOpenMapView(false); setGoToCurrentLocation(false) }}
                  />
                </p>
              </div>
              <MapView
                area={area}
                onSubmit={submitMapValue}
                defaultLocation={markerPosition}
                boundaryPoints={boundaryPoints}
                goToCurrentLocation={goToCurrentLocation}
              />
            </div>
          </div>
        </Modal>
      )}
      {!isLoading && (
        <div className="w-full h-full  rounded-xl  overflow-hidden relative">
          {useMap ? (
            <div
              role="button"
              className=" absolute z-[1] w-12 h-12 bg-white rounded-xl right-2 bottom-2 flex justify-center items-center cursor-pointer"
              onClick={() => setUseMap?.(false)}
            >
              <BsTrash size={24} color={"#008AF6"} />
            </div>
          ) : (
            <div className="w-full h-full z-[1]  backdrop-blur-[2px]  absolute inset-0 rounded-xl flex justify-center items-center">
              <Button
                size="slim"
                className="h-12 shadow-md font-normal text-base mb-2 m-auto rounded-lg w-72 mt-3"
                theme="blue"
                onClick={() => {
                  setUseMap?.(true);
                  setOpenMapView(true);
                }}
              >
                {label}
              </Button>
            </div>
          )}
          <GoogleMap
            options={{
              zoomControl: false,
              mapTypeControl: false,
              scaleControl: false,
              streetViewControl: false,
              rotateControl: false,
              fullscreenControl: false,
            }}
            yesIWantToUseGoogleMapApiInternals
            center={center}
            defaultZoom={13}
            onClick={() => setOpenMapView(true)}
          >
            <Marker
              key={"place.id"}
              text={"place.name"}
              lat={markerPosition.lat}
              lng={markerPosition.lng}
            />
          </GoogleMap>
        </div>
      )}
    </div>
  );
};

export default MapField;
